(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.EverteeBundleCore = factory();
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  class BundleValidationError extends Error {
    constructor(code, message, details) {
      super(message);
      this.name = 'BundleValidationError';
      this.code = code;
      this.details = details || {};
    }
  }
  const fail = (code, message, details) => {
    throw new BundleValidationError(code, message, details);
  };
  const positiveInteger = value => Number.isSafeInteger(value) && value > 0;
  const optionKey = values => JSON.stringify(values);
  const forbiddenKeys = new Set(['__proto__', 'prototype', 'constructor']);

  function optionList(values, name) {
    if (!Array.isArray(values) || values.length === 0 ||
        values.some(value => typeof value !== 'string' || !value.trim()) ||
        new Set(values).size !== values.length) {
      fail('INVALID_CONFIG', name + ' must contain distinct, non-empty strings.');
    }
    return values.slice();
  }

  function variantId(value) {
    if (typeof value === 'number' && !positiveInteger(value)) {
      fail('INVALID_VARIANT', 'Numeric variant IDs must be positive safe integers.');
    }
    const id = typeof value === 'number' || typeof value === 'string' ? String(value) : '';
    if (!/^[1-9][0-9]*$/.test(id)) fail('INVALID_VARIANT', 'A real numeric Shopify variant ID is required.');
    return id;
  }

  function quantityRule(rule) {
    if (rule === undefined) return null;
    if (!rule || typeof rule !== 'object' ||
        !positiveInteger(rule.minimum) || !positiveInteger(rule.increment) ||
        !(rule.maximum === null || positiveInteger(rule.maximum)) ||
        (rule.maximum !== null && rule.maximum < rule.minimum)) {
      fail('INVALID_QUANTITY_RULE', 'Supplied quantity rules must be complete and valid.');
    }
    return {minimum: rule.minimum, maximum: rule.maximum, increment: rule.increment};
  }

  function assertOwnedForm(form) {
    if (!form || typeof form.querySelectorAll !== 'function' ||
        !form.hasAttribute('data-evertee-owned-bundle-form')) {
      fail('UNOWNED_FORM', 'Use a dedicated form marked data-evertee-owned-bundle-form.');
    }
  }

  function clearNativeForm(form) {
    assertOwnedForm(form);
    Array.from(form.querySelectorAll('[name]')).forEach(field => {
      const name = field.getAttribute('name');
      if (name === 'id' || name === 'quantity' || name.startsWith('properties[') ||
          name.startsWith('shopify-additional-items[')) field.remove();
    });
  }

  const bundlePropertyKeys = Object.freeze(['_et_bundle_id', '_et_bundle_count', '_et_bundle_version']);
  const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

  function createBundleId(cryptoSource) {
    if (typeof cryptoSource?.randomUUID === 'function') {
      const id = cryptoSource.randomUUID();
      if (!uuidPattern.test(id)) fail('ENTROPY_UNAVAILABLE', 'A secure bundle reference could not be created.');
      return id.toLowerCase();
    }
    if (typeof cryptoSource?.getRandomValues !== 'function') {
      fail('ENTROPY_UNAVAILABLE', 'A secure bundle reference could not be created.');
    }
    const bytes = cryptoSource.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 15) | 64;
    bytes[8] = (bytes[8] & 63) | 128;
    const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
    return [hex.slice(0, 8), hex.slice(8, 12), hex.slice(12, 16), hex.slice(16, 20), hex.slice(20)].join('-');
  }

  function bundleProperties(id, count) {
    if (!uuidPattern.test(id) || ![1, 3, 5, 10].includes(count)) {
      fail('INVALID_GROUP', 'The bundle reference and pack count must be valid.');
    }
    return {_et_bundle_id: id, _et_bundle_count: String(count), _et_bundle_version: '1'};
  }

  function normalisedItem(item) {
    if (!item || !positiveInteger(item.quantity) || !item.properties ||
        typeof item.properties !== 'object' || Array.isArray(item.properties)) return null;
    const properties = Object.entries(item.properties).sort(([a], [b]) => a.localeCompare(b));
    if (properties.some(([key, value]) => !bundlePropertyKeys.includes(key) || typeof value !== 'string') ||
        properties.length !== bundlePropertyKeys.length) return null;
    const property = item.properties;
    const paid = ['1', '3', '5', '10'].includes(property._et_bundle_count) && ['1','OCTPREMIUM:1','GREEN15:1','MIGRATION:1'].includes(property._et_bundle_version);
    const gift = property._et_bundle_count === 'gift' && property._et_bundle_version === 'FREETEES:1';
    if (!uuidPattern.test(property._et_bundle_id) || (!paid && !gift) || item.selling_plan_allocation || item.selling_plan) return null;
    let id;
    try { id = variantId(item.variant_id ?? item.id); } catch (_) { return null; }
    return JSON.stringify([id, item.quantity, properties]);
  }

  function exactItems(actual, expected) {
    if (!Array.isArray(actual) || !Array.isArray(expected) || actual.length !== expected.length || !expected.length) return false;
    const left = actual.map(normalisedItem);
    const right = expected.map(normalisedItem);
    if (left.some(item => item === null) || right.some(item => item === null) ||
        new Set(right).size !== right.length || new Set(left).size !== left.length) return false;
    return JSON.stringify(left.sort()) === JSON.stringify(right.sort());
  }

  function inspectCartGroup(cart, expected, totalCents, currency) {
    if (!cart || !Array.isArray(cart.items) || !Array.isArray(expected) || !expected.length ||
        !Number.isSafeInteger(totalCents) || totalCents < 0 || cart.currency !== currency) {
      fail('CART_UNVERIFIED', 'Your bag could not be checked.');
    }
    const id = expected[0].properties?._et_bundle_id;
    if (!uuidPattern.test(id) || expected.some(item => item.properties?._et_bundle_id !== id)) {
      fail('INVALID_GROUP', 'The bundle reference is invalid.');
    }
    const actual = cart.items.filter(item => item.properties?._et_bundle_id === id);
    if (!actual.length) return {state: 'absent', items: []};
    const giftsFree = actual.every(item => item.properties._et_bundle_count !== 'gift' || item.final_line_price === 0);
    const priceMatches = giftsFree && actual.every(item => Number.isSafeInteger(item.final_line_price) && item.final_line_price >= 0) &&
      actual.reduce((sum, item) => sum + item.final_line_price, 0) === totalCents;
    return {state: exactItems(actual, expected) && priceMatches ? 'exact' : 'mismatch', items: actual};
  }

  function assertNativeEntries(form, entries) {
    assertOwnedForm(form);
    const relevant = Array.from(form.querySelectorAll('[name]')).filter(field => {
      const name = field.getAttribute('name');
      return name === 'id' || name === 'quantity' || name === 'selling_plan' || name === 'items' ||
        name.startsWith('items[') || name.startsWith('properties[') || name.startsWith('shopify-additional-items[');
    });
    if (relevant.some(field => field.disabled || field.type !== 'hidden')) {
      fail('INVALID_NATIVE_FORM', 'The bundle payment form has changed.');
    }
    const actual = relevant.map(field => [field.getAttribute('name'), field.value]).sort(([a], [b]) => a.localeCompare(b));
    const expected = entries.map(entry => entry.slice()).sort(([a], [b]) => a.localeCompare(b));
    if (JSON.stringify(actual) !== JSON.stringify(expected)) {
      fail('INVALID_NATIVE_FORM', 'The bundle payment form has changed.');
    }
    return true;
  }

  function createBundle(config) {
    if (!config || typeof config !== 'object') fail('INVALID_CONFIG', 'Bundle configuration is required.');
    const colours = optionList(config.colours, 'colours');
    const sizes = optionList(config.sizes, 'sizes');
    const finishes = optionList(config.finishes, 'finishes');
    const counts = config.allowedCounts;
    if (!Array.isArray(counts) || !counts.length || counts.some(value => !positiveInteger(value)) ||
        new Set(counts).size !== counts.length) {
      fail('INVALID_CONFIG', 'allowedCounts must contain distinct positive safe integers.');
    }
    const allowedCounts = counts.slice();
    let count = config.initialCount === undefined ? allowedCounts[0] : config.initialCount;
    if (!allowedCounts.includes(count)) fail('INVALID_COUNT', 'The initial pack count must be allowed.');
    const initialColour = config.initialColour === undefined ? null : config.initialColour;
    let finish = config.initialFinish === undefined ? null : config.initialFinish;
    if (initialColour !== null && !colours.includes(initialColour)) fail('INVALID_COLOUR', 'Unknown initial colour.');
    if (finish !== null && !finishes.includes(finish)) fail('INVALID_FINISH', 'Unknown initial finish.');
    let size = null;
    let explicitSize = false;
    let properties = {};
    const rows = Array.from({length: count}, () => ({colour: initialColour}));
    const propertyKeys = config.allowedProperties === undefined ? [] : config.allowedProperties;
    if (!Array.isArray(propertyKeys) || propertyKeys.some(key => typeof key !== 'string' ||
        !/^[A-Za-z0-9_][A-Za-z0-9 _.-]{0,79}$/.test(key) || forbiddenKeys.has(key))) {
      fail('INVALID_CONFIG', 'Property keys must be explicitly allowlisted safe strings.');
    }
    const allowedProperties = new Set(propertyKeys);
    if (!Array.isArray(config.variants) || !config.variants.length) fail('INVALID_VARIANT', 'Verified variants are required.');
    const variants = new Map();
    const ids = new Set();
    config.variants.forEach(source => {
      if (!source || !Array.isArray(source.options) || source.options.length !== 3 ||
          !colours.includes(source.options[0]) || !sizes.includes(source.options[1]) ||
          !finishes.includes(source.options[2]) || typeof source.available !== 'boolean') {
        fail('INVALID_VARIANT', 'Every variant needs known options and explicit availability.');
      }
      const id = variantId(source.id);
      const key = optionKey(source.options);
      if (variants.has(key) || ids.has(id)) fail('AMBIGUOUS_VARIANT', 'Variant IDs and option combinations must be unique.');
      ids.add(id);
      variants.set(key, {id, available: source.available, rule: quantityRule(source.quantityRule)});
    });

    function getState() {
      return {count, size, explicitSize, finish, rows: rows.slice(0, count).map(row => ({...row})), properties: {...properties}};
    }

    function setCount(value) {
      if (!positiveInteger(value) || !allowedCounts.includes(value)) fail('INVALID_COUNT', 'This pack count is not allowed.');
      while (rows.length < value) rows.push({colour: initialColour});
      count = value;
      return getState();
    }

    function setColour(index, value) {
      if (!Number.isSafeInteger(index) || index < 0 || index >= count) fail('INVALID_ROW', 'Choose an active bundle row.');
      if (value !== null && !colours.includes(value)) fail('INVALID_COLOUR', 'Choose a known colour.');
      rows[index].colour = value;
      return getState();
    }

    function setSize(value) {
      if (value !== null && !sizes.includes(value)) fail('INVALID_SIZE', 'Choose a known size.');
      size = value;
      explicitSize = value !== null;
      return getState();
    }

    function setFinish(value) {
      if (value !== null && !finishes.includes(value)) fail('INVALID_FINISH', 'Choose a known finish.');
      finish = value;
      return getState();
    }

    function setProperties(values) {
      if (!values || typeof values !== 'object' || Array.isArray(values)) fail('INVALID_PROPERTY', 'Properties must be an object.');
      const next = {};
      Object.entries(values).forEach(([key, value]) => {
        if (!allowedProperties.has(key) || typeof value !== 'string' || value.length > 1024 || /[\u0000-\u001f\u007f]/.test(value)) {
          fail('INVALID_PROPERTY', 'Only allowlisted text properties can be submitted.');
        }
        next[key] = value;
      });
      properties = next;
      return getState();
    }

    function resolve() {
      if (!explicitSize || size === null) fail('SIZE_REQUIRED', "Don't forget to select your size");
      if (finish === null) fail('FINISH_REQUIRED', 'Choose your finish.');
      const grouped = new Map();
      rows.slice(0, count).forEach((row, index) => {
        if (row.colour === null) fail('COLOUR_REQUIRED', 'Choose a colour for each T-shirt.', {row: index});
        const variant = variants.get(optionKey([row.colour, size, finish]));
        if (!variant) fail('VARIANT_NOT_FOUND', 'This exact combination could not be verified.', {row: index});
        if (!variant.available) fail('VARIANT_UNAVAILABLE', 'This combination is unavailable.', {row: index, id: variant.id});
        const line = grouped.get(variant.id) || {variant, quantity: 0};
        line.quantity += 1;
        grouped.set(variant.id, line);
      });
      const items = Array.from(grouped.values()).map(({variant, quantity}) => {
        const rule = variant.rule;
        if (rule && (quantity < rule.minimum || (rule.maximum !== null && quantity > rule.maximum) || quantity % rule.increment !== 0)) {
          fail('QUANTITY_RULE', 'The quantity does not meet this variant\'s rules.', {id: variant.id, quantity, rule: {...rule}});
        }
        const item = {id: variant.id, quantity};
        if (Object.keys(properties).length) item.properties = {...properties};
        return item;
      });
      return {items, totalQuantity: count};
    }

    function toCartPayload() {
      return {items: resolve().items};
    }

    function toNativeFormEntries() {
      const entries = [];
      resolve().items.forEach((item, index) => {
        const name = field => index === 0 ? field : 'shopify-additional-items[' + index + '][' + field + ']';
        entries.push([name('id'), item.id], [name('quantity'), String(item.quantity)]);
        const properties = Object.entries(item.properties || {});
        if (index === 0) {
          properties.forEach(([key, value]) => entries.push(['properties[' + key + ']', value]));
        } else {
          entries.push([name('properties'), JSON.stringify(properties.map(([key, value]) => ({key, value})))]);
        }
      });
      return entries;
    }

    function syncNativeForm(form) {
      clearNativeForm(form);
      const entries = toNativeFormEntries();
      const fragment = form.ownerDocument.createDocumentFragment();
      entries.forEach(([name, value]) => {
        const input = form.ownerDocument.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        input.setAttribute('data-evertee-bundle-input', '');
        fragment.appendChild(input);
      });
      form.appendChild(fragment);
      return entries;
    }

    return Object.freeze({getState, setCount, setColour, setSize, setFinish, setProperties,
      resolve, toCartPayload, toNativeFormEntries, syncNativeForm});
  }

  return Object.freeze({createBundle, clearNativeForm, BundleValidationError, bundlePropertyKeys, createBundleId, bundleProperties, exactItems, inspectCartGroup, assertNativeEntries});
});


